<template>
  <div class="actividades-editar">
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Editar Actividad</h1>
        <p class="content-header-subtitle">Modificar los datos de la actividad</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
      </div>
    </div>

    <b-card>
      <div v-if="cargando" class="text-center py-4">
        <b-spinner variant="primary" label="Cargando..."></b-spinner>
        <p class="mt-2">Cargando datos del formulario...</p>
      </div>
      
      <b-form v-else @submit.prevent="guardarActividad">
        <b-row>
          <b-col md="6">
            <b-form-group label="Tipo de Actividad" required>
              <b-form-select v-model="actividad.tipoActividad" :options="opcionesTipoActividad" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Fecha" required>
              <b-form-datepicker 
                v-model="actividad.fecha" 
                :start-weekday="1"
                required 
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Estado de Actividad" required>
              <b-form-select v-model="actividad.estadoActividad" :options="opcionesEstadoActividad" required />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Proyecto">
              <b-form-select v-model="actividad.proyecto" :options="opcionesProyecto" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Próxima Acción Fecha" required>
              <b-form-datepicker 
                v-model="actividad.proximaAccionFecha" 
                :start-weekday="1"
                required 
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Próxima Acción Tipo" required>
              <b-form-select v-model="actividad.proximaAccionTipo" :options="opcionesAccionActividad" required />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Recordatorio">
              <b-form-checkbox v-model="actividad.recordatorio" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Observaciones">
          <b-form-textarea v-model="actividad.observaciones" rows="4" />
        </b-form-group>

        <div class="d-flex justify-content-end gap-2">
          <b-button variant="outline-secondary" @click="volver">Cancelar</b-button>
          <b-button type="submit" variant="primary" :disabled="guardando">
            {{ guardando ? 'Guardando...' : 'Guardar Cambios' }}
          </b-button>
        </div>
      </b-form>

      <!-- Subida de documentos (opcional) -->
      <div class="mt-4">
        <b-card title="Adjuntar Documentos" class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <p class="text-muted mb-0">
              Puedes adjuntar documentos adicionales a esta actividad.
            </p>
            <b-button 
              variant="outline-primary" 
              @click="toggleSubidaDocumentos"
              :class="{ 'active': mostrarSubidaDocumentos }"
            >
              <feather-icon icon="PaperclipIcon" size="16" class="mr-1" />
              {{ mostrarSubidaDocumentos ? 'Ocultar' : 'Adjuntar Documentos' }}
            </b-button>
          </div>
          
          <div v-if="mostrarSubidaDocumentos">
            <SubidaDocumentos 
              :id-actividad="actividad.idActividad"
              :id-usuario="usuarioActual.idUsuario"
              @documento-subido="onDocumentoSubido"
            />
          </div>
        </b-card>
      </div>
    </b-card>
  </div>
</template>

<script>
import { TipoActividadService } from '@/services/tipoActividadService'
import { EstadoActividadService } from '@/services/estadoActividadService'
import { AccionActividadService } from '@/services/accionActividadService'
import { ProyectoService } from '@/services/proyectoService'
import { ActividadService } from '@/services/actividadService'
import { getUserData } from '@/auth/utils'
import SubidaDocumentos from '@/components/SubidaDocumentos.vue'

export default {
  name: 'ActividadesEditar',
  components: {
    SubidaDocumentos
  },
  data() {
    return {
      guardando: false,
      cargando: true,
      actividad: {
        idActividad: null,
        tipoActividad: null,
        fecha: '',
        estadoActividad: null,
        observaciones: '',
        proximaAccionFecha: '',
        proximaAccionTipo: null,
        recordatorio: false,
        proyecto: null,
      },
      opcionesTipoActividad: [
        { value: null, text: 'Seleccionar tipo de actividad' }
      ],
      opcionesEstadoActividad: [
        { value: null, text: 'Seleccionar estado' }
      ],
      opcionesAccionActividad: [
        { value: null, text: 'Seleccionar acción' }
      ],
      opcionesProyecto: [
        { value: null, text: 'Seleccionar proyecto (opcional)' }
      ],
      mostrarSubidaDocumentos: false,
      usuarioActual: {
        idUsuario: 1 // TODO: Obtener del store de autenticación
      },
    }
  },
  async mounted() {
    await this.cargarDatosIniciales()
  },
  methods: {
    async cargarDatosIniciales() {
      this.cargando = true
      try {
        console.log('🔄 Iniciando carga de datos iniciales...')
        
        // Cargar todos los datos en paralelo
        const [tiposActividad, estadosActividad, accionesActividad, proyectos, actividadActual] = await Promise.all([
          TipoActividadService.getAll(),
          EstadoActividadService.getAll(),
          AccionActividadService.getAll(),
          ProyectoService.getAll(),
          this.cargarActividadActual()
        ])

        console.log('📊 Datos recibidos del backend:')
        console.log('Tipos de actividad:', tiposActividad)
        console.log('Estados de actividad:', estadosActividad)
        console.log('Acciones de actividad:', accionesActividad)
        console.log('Proyectos:', proyectos)
        console.log('Actividad actual:', actividadActual)

        // Mapear tipos de actividad
        this.opcionesTipoActividad = [
          { value: null, text: 'Seleccionar tipo de actividad' },
          ...tiposActividad.map(tipo => ({
            value: tipo.idTipoActividad,
            text: tipo.nombre
          }))
        ]

        // Mapear estados de actividad
        this.opcionesEstadoActividad = [
          { value: null, text: 'Seleccionar estado' },
          ...estadosActividad.map(estado => ({
            value: estado.idEstadoActividad,
            text: estado.nombre
          }))
        ]

        // Mapear acciones de actividad
        this.opcionesAccionActividad = [
          { value: null, text: 'Seleccionar acción' },
          ...accionesActividad.map(accion => ({
            value: accion.idAccionActividad,
            text: accion.nombre
          }))
        ]

        // Mapear proyectos
        this.opcionesProyecto = [
          { value: null, text: 'Seleccionar proyecto (opcional)' },
          ...proyectos.map(proyecto => ({
            value: proyecto.idProyecto,
            text: proyecto.nombre
          }))
        ]

        // Cargar datos de la actividad actual
        if (actividadActual) {
          this.actividad = {
            idActividad: actividadActual.idActividad,
            tipoActividad: actividadActual.idTipoActividad,
            fecha: actividadActual.fecha,
            estadoActividad: actividadActual.idEstadoActividad,
            observaciones: actividadActual.observaciones || '',
            proximaAccionFecha: actividadActual.proximaAccionFecha,
            proximaAccionTipo: actividadActual.idProximaAccionTipo,
            recordatorio: actividadActual.recordatorio || false,
            proyecto: actividadActual.idProyecto || null,
          }
        }

        console.log('🎉 Carga de datos completada exitosamente')

      } catch (error) {
        console.error('Error al cargar datos iniciales:', error)
        this.$toast({
          message: 'Error al cargar los datos del formulario',
          variant: 'danger',
        })
      } finally {
        this.cargando = false
      }
    },

    async cargarActividadActual() {
      try {
        const actividadId = parseInt(this.$route.params.id)
        return await ActividadService.getById(actividadId)
      } catch (error) {
        console.error('Error al cargar la actividad:', error)
        this.$toast({
          message: 'Error al cargar la actividad',
          variant: 'danger',
        })
        return null
      }
    },

    async guardarActividad() {
      this.guardando = true
      try {
        // Validar campos obligatorios
        if (!this.actividad.tipoActividad || !this.actividad.fecha || !this.actividad.estadoActividad || !this.actividad.proximaAccionFecha || !this.actividad.proximaAccionTipo) {
          this.$toast({
            message: 'Por favor, complete todos los campos obligatorios',
            variant: 'warning',
          })
          return
        }

        // Obtener el usuario actual
        const userData = getUserData()
        const idCreadoPor = userData?.idUsuario || 1 // Usar ID del usuario actual o 1 por defecto

        // Preparar los datos para enviar al backend
        const actividadData = {
          idActividad: this.actividad.idActividad,
          idTipoActividad: this.actividad.tipoActividad,
          idEstadoActividad: this.actividad.estadoActividad,
          fecha: this.actividad.fecha,
          proximaAccionFecha: this.actividad.proximaAccionFecha,
          idProximaAccionTipo: this.actividad.proximaAccionTipo,
          observaciones: this.actividad.observaciones || '',
          recordatorio: this.actividad.recordatorio,
          idCreadoPor: idCreadoPor,
          idProyecto: this.actividad.proyecto || null
        }

        console.log('📤 Enviando datos de actividad al backend:', actividadData)
        
        // Llamar al servicio para actualizar la actividad
        const actividadActualizada = await ActividadService.update(this.actividad.idActividad, actividadData)
        
        console.log('✅ Actividad actualizada exitosamente:', actividadActualizada)
        
        this.$toast({
          message: 'Actividad actualizada correctamente',
          variant: 'success',
        })
        this.$router.push({ name: 'actividades-detalle', params: { id: this.actividad.idActividad } })
      } catch (error) {
        console.error('Error al guardar actividad:', error)
        this.$toast({
          message: 'Error al actualizar la actividad',
          variant: 'danger',
        })
      } finally {
        this.guardando = false
      }
    },

    volver() {
      this.$router.push({ name: 'actividades-lista' })
    },

    onDocumentoSubido() {
      this.$toast({
        message: 'Documento subido correctamente',
        variant: 'success',
      })
      // Opcional: ir al detalle de la actividad
      this.$router.push({ name: 'actividades-detalle', params: { id: this.actividad.idActividad } })
    },

    toggleSubidaDocumentos() {
      this.mostrarSubidaDocumentos = !this.mostrarSubidaDocumentos
    },
  },
}
</script>

<style lang="scss" scoped>
.actividades-editar {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.gap-2 {
  gap: 0.5rem;
}
</style>
